import React, { useMemo, useState, useEffect } from "react";
import { GripVertical, Plus, Pencil, Trash2, Search, Save, X, ChevronDown, ChevronRight, Folder, FileText } from "lucide-react";
import "../styles/TreeEdit.css";

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `node-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};


async function putUpdatedCallTypes(dataArray, deletedIdsArray) {
  const baseUrl = process.env.REACT_APP_API_URL;
  const apiUrl = (baseUrl.endsWith("/") ? baseUrl : baseUrl + "/") + "admin/master-data/CallType";
  const headers = { "Content-Type": "application/json", Accept: "application/json" };
  if (process.env.REACT_APP_API_AUTHORIZATION) headers.Authorization = process.env.REACT_APP_API_AUTHORIZATION;

  const body = { data: dataArray, deletedIds: deletedIdsArray || [] };
  // eslint-disable-next-line no-console
  console.log('PUT update CallType (via helper)', apiUrl, body);
  const resp = await fetch(apiUrl, { method: 'PUT', headers, body: JSON.stringify(body) });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText || '');
    throw new Error(`API update error ${resp.status} ${resp.statusText}: ${text}`);
  }
  const result = await resp.json();
  return result;
}

function flattenTreeForPut(nodes, parentValue = null) {
  const result = [];
  for (let i = 0; i < (nodes || []).length; i += 1) {
    const node = nodes[i];
    if (!node) continue;
    const meta = node._meta || {};
    const obj = {};
    // only include numeric server ids
    if (meta.value != null && !Number.isNaN(Number(meta.value))) obj.id = Number(meta.value);
    obj.name = node.name;
    obj.helpText = meta.help_text ?? meta.helpText ?? "";
    obj.parentId = parentValue == null ? null : (!Number.isNaN(Number(parentValue)) ? Number(parentValue) : null);
    obj.sortOrder = Number(meta.sort_order || i + 1);
    obj.isActive = meta.is_active != null ? meta.is_active : 1;
    obj.isVisibleInReport = meta.isVisibleInReport != null ? meta.isVisibleInReport : 1;
    result.push(obj);
    const childParentValue = meta.value != null ? meta.value : null;
    const children = flattenTreeForPut(node.children || [], childParentValue);
    result.push(...children);
  }
  return result;
}

function cloneTree(tree) {
  return JSON.parse(JSON.stringify(tree));
}

function assignSortOrders(nodes) {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.map((node, idx) => {
    const children = assignSortOrders(node.children || []);
    const meta = { ...(node._meta || {}), sort_order: idx + 1 };
    return { ...node, children, _meta: meta };
  });
}

function findNodeById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    const child = findNodeById(n.children || [], id);
    if (child) return child;
  }
  return null;
}

function findParentId(nodes, id) {
  for (const n of nodes) {
    if ((n.children || []).some((c) => c.id === id)) {
      // prefer meta.value if present
      return n._meta && n._meta.value != null ? n._meta.value : null;
    }
    const deeper = findParentId(n.children || [], id);
    if (deeper != null) return deeper;
  }
  return null;
}

function findParentNode(nodes, id) {
  for (const n of nodes) {
    if ((n.children || []).some((c) => c.id === id)) return n;
    const deeper = findParentNode(n.children || [], id);
    if (deeper) return deeper;
  }
  return null;
}


async function postNewNodeToServer(node, parentValue, sortOrder) {
  try {
    const baseUrl = process.env.REACT_APP_API_URL;
    const apiUrl = (baseUrl.endsWith("/") ? baseUrl : baseUrl + "/") + "admin/master-data/CallType";
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    if (process.env.REACT_APP_API_AUTHORIZATION) headers.Authorization = process.env.REACT_APP_API_AUTHORIZATION;

    const body = {
      name: node.name,
      helpText: (node._meta && (node._meta.help_text || node._meta.helpText)) || "",
      parentId: parentValue == null ? null : parentValue,
      sortOrder: sortOrder || (node._meta && node._meta.sort_order) || 0,
      isActive: 1,
    };

    // eslint-disable-next-line no-console
    console.log('POST create CallType', apiUrl, body);

    const resp = await fetch(apiUrl, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!resp.ok) {
      const text = await resp.text().catch(() => resp.statusText || '');
      throw new Error(`API create error ${resp.status} ${resp.statusText}: ${text}`);
    }
    const data = await resp.json();
    return data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to create CallType on server', err);
    throw err;
  }
}

function mapFromApiNode(node) {
  if (!node) return null;
  const mapped = {
    id: node.value != null ? String(node.value) : createId(),
    name: node.label ?? node.name ?? "",
    expanded: node.expanded ?? true,
    // preserve children order based on sort_order from API
    children: (node.children || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(mapFromApiNode),
    _meta: { ...node }
  };
  return mapped;
}

function mapToApiNode(node, index) {
  if (!node) return null;
  const meta = node._meta || {};
  const value = meta.value != null ? meta.value : undefined;
  const children = (node.children || []).map((c, i) => mapToApiNode(c, i));
  // Preserve original meta fields when present, but ensure label/children/ sort_order updated
  const apiNode = { ...meta, label: node.name, children };
  if (value !== undefined) apiNode.value = value;
  // set sort_order based on position within parent (1-based). If meta already had a sort_order prefer new position
  if (typeof index === "number") apiNode.sort_order = index + 1;
  else if (apiNode.sort_order == null) apiNode.sort_order = 0;
  // Remove internal-only fields that shouldn't be sent
  delete apiNode.expanded;
  return apiNode;
}

function updateNode(nodes, id, updater) {
  return nodes.map((node) => {
    if (node.id === id) return updater(node);
    return { ...node, children: updateNode(node.children || [], id, updater) };
  });
}

function deleteNodeById(nodes, id) {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({ ...node, children: deleteNodeById(node.children || [], id) }));
}

function findNodePath(nodes, id, path = []) {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const currentPath = [...path, index];
    if (node.id === id) return currentPath;
    const childPath = findNodePath(node.children || [], id, [...currentPath, "children"]);
    if (childPath) return childPath;
  }
  return null;
}

function getArrayAtPath(root, pathToArray) {
  let ref = root;
  for (const part of pathToArray) ref = ref[part];
  return ref;
}

function getParentArrayPath(path) {
  return path.slice(0, -1);
}

function moveNodeWithinSameLevel(nodes, sourceId, targetId, position) {
  const draft = cloneTree(nodes);
  const sourcePath = findNodePath(draft, sourceId);
  const targetPath = findNodePath(draft, targetId);

  if (!sourcePath || !targetPath) return nodes;

  const sourceParentPath = getParentArrayPath(sourcePath);
  const targetParentPath = getParentArrayPath(targetPath);

  if (JSON.stringify(sourceParentPath) !== JSON.stringify(targetParentPath)) {
    return nodes;
  }

  const siblings = getArrayAtPath(draft, sourceParentPath);
  const sourceIndex = sourcePath[sourcePath.length - 1];
  let targetIndex = targetPath[targetPath.length - 1];
  const [movingNode] = siblings.splice(sourceIndex, 1);

  if (sourceIndex < targetIndex) targetIndex -= 1;
  if (position === "after") targetIndex += 1;

  siblings.splice(targetIndex, 0, movingNode);
  return draft;
}

function doesNodeMatch(node, query) {
  if (!query) return true;
  const ownMatch = node.name.toLowerCase().includes(query);
  const childMatch = (node.children || []).some((child) => doesNodeMatch(child, query));
  return ownMatch || childMatch;
}

function TreeNode({
  node,
  level,
  searchText,
  editingId,
  setEditingId,
  editValue,
  setEditValue,
  onSaveEdit,
  onAddChild,
  onDelete,
  onToggle,
  dragState,
  setDragState,
  onDropNode,
  selectedId,
  onSelect
}) {
  const hasChildren = (node.children || []).length > 0;
  const filteredChildren = (node.children || []).filter((child) => doesNodeMatch(child, searchText));
  const isEditing = editingId === node.id;

  const startEdit = () => {
    setEditingId(node.id);
    setEditValue(node.name);
  };

  const handleDragStart = (event) => {
    setDragState({ sourceId: node.id, targetId: null, position: null });
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", node.id);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!dragState.sourceId || dragState.sourceId === node.id) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const position = event.clientY < rect.top + rect.height / 2 ? "before" : "after";
    setDragState((current) => ({ ...current, targetId: node.id, position }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (!dragState.sourceId || dragState.sourceId === node.id) return;
    onDropNode(dragState.sourceId, node.id, dragState.position || "after");
    setDragState({ sourceId: null, targetId: null, position: null });
  };

  const handleDragEnd = () => {
    setDragState({ sourceId: null, targetId: null, position: null });
  };

  return (
    <li className="tree-item">
      <div
        className={["tree-row", dragState.sourceId === node.id ? "is-dragging" : "", dragState.targetId === node.id && dragState.position === "before" ? "drop-before" : "", dragState.targetId === node.id && dragState.position === "after" ? "drop-after" : "", selectedId === node.id ? "is-selected" : ""].join(" ")}
        draggable={!isEditing}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        style={{ "--level": level }}
      >
        <button className="drag-button" title="Drag up or down" aria-label="Drag item">
          <GripVertical size={18} />
        </button>

        <button type="button" className={`toggle-button ${!hasChildren ? "is-empty" : ""}`} onClick={() => onToggle(node.id)} aria-label={node.expanded ? "Collapse" : "Expand"}>
          {node.expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className={`node-icon ${hasChildren ? "folder" : "file"}`}>
          {hasChildren ? <Folder size={18} /> : <FileText size={18} />}
        </div>

        <div className="node-content" onClick={() => onSelect && onSelect(node.id)} style={{ cursor: 'pointer' }}>
          {isEditing ? (
            <div className="inline-editor">
              <input autoFocus value={editValue} onChange={(event) => setEditValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") onSaveEdit(node.id); if (event.key === "Escape") setEditingId(null); }} />
              <button className="success-button" onClick={() => onSaveEdit(node.id)} title="Save"><Save size={16} /></button>
              <button className="muted-button" onClick={() => setEditingId(null)} title="Cancel"><X size={16} /></button>
            </div>
          ) : (
            <>
              <div className="node-title">{node.name}</div>
              <div className="node-subtitle">{hasChildren ? `${node.children.length} child item${node.children.length > 1 ? "s" : ""}` : "Leaf item"}</div>
            </>
          )}
        </div>

        {!isEditing && (
          <div className="node-actions">
            {level === 0 && (
              <button onClick={() => onAddChild(node.id)} title="Add child"><Plus size={16} /></button>
            )}
            <button onClick={startEdit} title="Rename"><Pencil size={16} /></button>
            <button className="danger" onClick={() => onDelete(node.id, node.name)} title="Delete"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      {node.expanded && filteredChildren.length > 0 && (
        <ul className="tree-children">
          {filteredChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              searchText={searchText}
              editingId={editingId}
              setEditingId={setEditingId}
              editValue={editValue}
              setEditValue={setEditValue}
              onSaveEdit={onSaveEdit}
              onAddChild={onAddChild}
              onDelete={onDelete}
              onToggle={onToggle}
              dragState={dragState}
              setDragState={setDragState}
              onDropNode={onDropNode}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function CategoryTreeEditor({ open, field, onClose, onSave }) {
  const [tree, setTree] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  // eslint-disable-next-line
  const [pendingCreates, setPendingCreates] = useState(0);
  const [dragState, setDragState] = useState({ sourceId: null, targetId: null, position: null });
  const [selectedId, setSelectedId] = useState(null);
  const pendingCreatesRef = React.useRef(0);

  useEffect(() => {
    if (!open) return;
    // initialize tree from field.tree if provided
    if (field && field.tree) {
      try {
        const mapped = (field.tree || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(mapFromApiNode);
        setTree(cloneTree(assignSortOrders(mapped)));
        return;
      } catch (e) {
        // ignore
      }
    }
    // load live data from API when opening editor
    (async () => {
      try {
        const name = (field && field.name ? String(field.name).toLowerCase() : "");
        if (!name.includes("call")) return;

        setLoading(true);
        setError(null);

        // build apiUrl; fall back to known dev host if env var not provided
          const baseUrl = process.env.REACT_APP_API_URL;
          const apiUrl = (baseUrl.endsWith("/") ? baseUrl : baseUrl + "/") + "admin/master-data/CallType";
        // eslint-disable-next-line no-console
        console.log('Fetching call types from', apiUrl);

        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
        if (process.env.REACT_APP_API_AUTHORIZATION) headers.Authorization = process.env.REACT_APP_API_AUTHORIZATION;

        const fetchOptions = { method: "GET", headers, mode: 'cors' };
        // if you need cookies / credentials set REACT_APP_API_USE_CREDENTIALS=include in env
        if (process.env.REACT_APP_API_USE_CREDENTIALS === 'include') fetchOptions.credentials = 'include';

        const response = await fetch(apiUrl, fetchOptions);

        if (!response.ok) {
          const text = await response.text().catch(() => response.statusText || '');
          const msg = `API error ${response.status} ${response.statusText}: ${text}`;
          // eslint-disable-next-line no-console
          console.error(msg);
          setError(msg);
          setLoading(false);
          return;
        }

        const res = await response.json();
        // eslint-disable-next-line no-console
        console.log('CallType response', res);

        // support multiple shapes: { call_types: [...] } or { data: { call_types: [...] } }
        const list = Array.isArray(res.call_types)
          ? res.call_types
          : res.data && Array.isArray(res.data.call_types)
          ? res.data.call_types
          : res.data && Array.isArray(res.data)
          ? res.data
          : null;

        if (Array.isArray(list)) {
          const mapped = list.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(mapFromApiNode);
          setTree(cloneTree(assignSortOrders(mapped)));
        } else {
          const msg = 'Unexpected API response shape: missing call_types';
          // eslint-disable-next-line no-console
          console.error(msg, res);
          setError(msg);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load call types from API', err);
        setError(String(err));
      } finally {
        setLoading(false);
      }
    })();
    // do not default to sample data; if no API data, tree remains empty
  }, [open, field]);

  const visibleTree = useMemo(() => tree.filter((node) => doesNodeMatch(node, searchText.trim().toLowerCase())), [tree, searchText]);

  const commitTree = (nextTree) => {
    setTree(nextTree);
  };

  const addRoot = () => {
    const newNode = { id: createId(), name: "New Root Item", expanded: true, children: [], _meta: { value: null } };
    const next = assignSortOrders([...tree, newNode]);
    commitTree(next);
    setEditingId(newNode.id);
    setEditValue(newNode.name);
    setSelectedId(newNode.id);
    // Do not POST on add; creation will occur when the item is saved (saveEdit) or on Save Changes
  };

  const addChild = (parentId) => {
    const newNode = { id: createId(), name: "New Child Item", expanded: true, children: [], _meta: { value: null } };
    const nextTree = updateNode(tree, parentId, (node) => ({ ...node, expanded: true, children: [...(node.children || []), newNode] }));
    const next = assignSortOrders(nextTree);
    commitTree(next);
    setEditingId(newNode.id);
    setEditValue(newNode.name);
    // Select the newly added child so user can edit help text immediately
    setSelectedId(newNode.id);
    // Do not POST on add; creation will occur when the item is saved (saveEdit) or on Save Changes
  };

  const saveEdit = async (id) => {
    const value = editValue.trim();
    if (!value) return;
    // Apply local name change immediately
    const nextTree = updateNode(tree, id, (node) => ({ ...node, name: value }));
    commitTree(nextTree);
    setEditingId(null);
    setEditValue("");

    try {
      // find the node we just edited in the updated tree
      const node = findNodeById(nextTree, id);
      if (!node) return;
      // if node has no server-side id (meta.value), POST it now
      const meta = node._meta || {};
      if (meta.value == null) {
        // mark a pending create so other actions (like Save Changes) can wait
        setPendingCreates((p) => {
          pendingCreatesRef.current = p + 1;
          return p + 1;
        });
        setLoading(true);
        setError(null);
        // determine parentValue and sortOrder
        const parentNode = findParentNode(nextTree, id);
        const parentValue = parentNode && parentNode._meta && parentNode._meta.value != null ? parentNode._meta.value : null;
        const siblingList = parentNode ? (parentNode.children || []) : nextTree;
        const sortOrder = siblingList.findIndex((n) => n.id === id) + 1;
        const created = await postNewNodeToServer(node, parentValue, sortOrder);
        const payload = created && created.data ? created.data : created;
        const newValue = payload && (payload.value != null ? payload.value : payload.id != null ? payload.id : null);
        if (newValue != null) {
          // ensure meta.value is present so future PUTs recognize this as an existing server record
          const mergedMeta = { ...(node._meta || {}), ...(payload || {}), value: newValue };
          const updated = updateNode(nextTree, id, (n) => ({ ...n, id: String(newValue), _meta: mergedMeta }));
          commitTree(assignSortOrders(updated));
        }
        // clear pending create marker
        setPendingCreates((p) => {
          const next = Math.max(0, p - 1);
          pendingCreatesRef.current = next;
          return next;
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to POST new item on saveEdit', err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = (id, name) => {
    const canDelete = window.confirm(`Delete "${name}" and all child items?`);
    if (!canDelete) return;
    // collect server ids from node and its subtree to include in deletedIds
    function collectIds(nodes, targetId) {
      const found = [];
      function walkAndCollect(node) {
        if (node && node._meta && node._meta.value != null) found.push(node._meta.value);
        for (const c of node.children || []) walkAndCollect(c);
      }
      const target = findNodeById(nodes, targetId);
      if (target) walkAndCollect(target);
      return found;
    }

    const ids = collectIds(tree, id).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
    if (ids.length) setDeletedIds((prev) => Array.from(new Set([...prev, ...ids])));

    const next = assignSortOrders(deleteNodeById(tree, id));
    commitTree(next);
    // Clear selection after deletion to avoid pointing to removed nodes
    setSelectedId(null);
  };

  const toggleItem = (id) => {
    const nextTree = updateNode(tree, id, (node) => ({ ...node, expanded: !node.expanded }));
    commitTree(nextTree);
  };

  const dropNode = (sourceId, targetId, position) => {
    const nextTree = moveNodeWithinSameLevel(tree, sourceId, targetId, position);
    const next = assignSortOrders(nextTree);
    commitTree(next);
  };

  const setHelpTextForNode = (id, helpText) => {
    const nextTree = updateNode(tree, id, (node) => ({ ...node, _meta: { ...(node._meta || {}), help_text: helpText } }));
    commitTree(nextTree);
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="edit-modal" style={{ width: '80%', maxWidth: 600 }}>
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div><h2 style={{ fontFamily: '"Futura PT", sans-serif', fontSize: '16px', fontWeight: 400, color: '#178b96', margin: 0 }}>EDIT CATEGORY TREE</h2></div>
                  <div style={{ fontFamily: '"Futura PT", sans-serif', fontSize: '16px', fontWeight: 400, opacity: 0.85 }}>FIELD NAME: {field && field.name ? field.name : ''}</div>
          <button onClick={onClose}><X /></button>
        </div>

        <div className="modal-body">
          <div className="modal-body-content">
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }} className="search-box">
                <Search size={18} />
                <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search tree items..." />
              </div>
              <div style={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 8 }}>
                {loading ? (
                  <div style={{ color: '#178b96', fontSize: 13 }}>Loading...</div>
                ) : error ? (
                  <div style={{ color: '#d9534f', fontSize: 13 }}>{error}</div>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 8 }} className="modal-footer">
                <button type="button" onClick={addRoot} className="primary-button">Add New Item</button>
              </div>
            </div>

            <ul className="tree-root">
              {visibleTree.length ? (
                visibleTree.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    level={0}
                    searchText={searchText.trim().toLowerCase()}
                    editingId={editingId}
                    setEditingId={setEditingId}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    onSaveEdit={saveEdit}
                    onAddChild={addChild}
                    onDelete={deleteItem}
                    onToggle={toggleItem}
                    dragState={dragState}
                    setDragState={setDragState}
                    onDropNode={dropNode}
                    selectedId={selectedId}
                    onSelect={(id) => setSelectedId(id)}
                  />
                ))
              ) : (
                <li className="empty-state">No matching items found.</li>
              )}
            </ul>

            {/* Help text editor has been moved below modal body to avoid being inside scrollable area */}
          </div>
        </div>

        {/* Help text editor for selected node placed outside of scrollable modal-body */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #eee' }}>
          {selectedId ? (
            (() => {
              function findNode(nodes, id) {
                for (const n of nodes) {
                  if (n.id === id) return n;
                  const child = findNode(n.children || [], id);
                  if (child) return child;
                }
                return null;
              }

              const sel = findNode(tree, selectedId);
              const currentHelp = (sel && sel._meta && (sel._meta.help_text || sel._meta.helpText || "")) || "";
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontFamily: '"Futura PT", sans-serif', fontSize: 14, color: '#178b96', marginBottom: 4 }}>Help Text</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <textarea
                      value={currentHelp}
                      onChange={(e) => setHelpTextForNode(selectedId, e.target.value)}
                      rows={3}
                      style={{ flex: 1, minWidth: 0, padding: 8, fontFamily: 'inherit', fontSize: 14 }}
                      placeholder="Enter help text for the selected item..."
                    />

                          <div style={{ display: 'flex', alignItems: 'flex-start' }} className="modal-footer">
                      <button
                        type="button"
                        className="primary-button"
                        onClick={() => {
                          // persist help text for the selected node only, keep popup open
                          (async () => {
                            try {
                              setLoading(true);
                              setError(null);
                              // find selected node
                              const sel = findNodeById(tree, selectedId);
                              if (!sel) {
                                setError('No item selected');
                                return;
                              }
                              // determine parent server id if any
                              const parentValue = findParentId(tree, sel.id);
                              // If the selected node does not have a server id yet, POST it instead of PUT
                              if (!sel._meta || sel._meta.value == null) {
                                // determine sortOrder among siblings
                                const parentNode = findParentNode(tree, sel.id);
                                const siblingList = parentNode ? (parentNode.children || []) : tree;
                                const sortOrder = siblingList.findIndex((n) => n.id === sel.id) + 1;
                                // mark pending create
                                setPendingCreates((p) => {
                                  pendingCreatesRef.current = p + 1;
                                  return p + 1;
                                });
                                const created = await postNewNodeToServer(sel, parentValue, sortOrder);
                                const payload = created && created.data ? created.data : created;
                                if (payload && (payload.value != null || payload.id != null)) {
                                  const returnedId = payload.value != null ? payload.value : payload.id;
                                  const mergedMeta = { ...(sel._meta || {}), ...(payload || {}), value: returnedId };
                                  const updated = updateNode(tree, sel.id, (node) => ({ ...node, id: String(returnedId), _meta: mergedMeta }));
                                  commitTree(assignSortOrders(updated));
                                }
                                // clear pending create
                                setPendingCreates((p) => {
                                  const next = Math.max(0, p - 1);
                                  pendingCreatesRef.current = next;
                                  return next;
                                });
                              } else {
                                const data = flattenTreeForPut([sel], parentValue);
                                // call PUT for this single item (or subtree)
                                const result = await putUpdatedCallTypes(data, []);
                                // eslint-disable-next-line no-console
                                console.log('Save help result', result);
                                // optionally update meta from response if present
                                const payload = result && result.data ? result.data : result;
                                // if server returned updated id/value, merge into node
                                if (Array.isArray(payload) && payload.length > 0) {
                                  const returned = payload[0];
                                  if (returned && returned.id != null) {
                                    const updated = updateNode(tree, sel.id, (node) => ({ ...node, id: String(returned.id), _meta: { ...(node._meta || {}), ...returned } }));
                                    commitTree(assignSortOrders(updated));
                                  }
                                }
                              }
                            } catch (err) {
                              // eslint-disable-next-line no-console
                              console.error('Failed to save help text', err);
                              setError(String(err));
                            } finally {
                              setLoading(false);
                            }
                          })();
                        }}
                      >
                        Save Help
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={{ fontSize: 13, color: '#666' }}>Select an item to edit its help text.</div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose}>Cancel</button>
          <button
            onClick={() => {
              (async () => {
                try {
                  // wait for any in-flight creates to finish to avoid duplicate creation on server
                  const waitUntilZero = async (timeoutMs = 5000) => {
                    const start = Date.now();
                    // eslint-disable-next-line no-await-in-loop
                    while (pendingCreatesRef.current > 0) {
                      if (Date.now() - start > timeoutMs) break;
                      // wait a short time
                      // eslint-disable-next-line no-await-in-loop
                      await new Promise((r) => setTimeout(r, 100));
                    }
                  };
                  await waitUntilZero();
                  setLoading(true);
                  setError(null);
                  // ensure sort orders are assigned
                  const current = assignSortOrders(tree);
                  // build payload for PUT but exclude any nodes that do not yet have a server id
                  const allData = flattenTreeForPut(current, null);
                  const data = (allData || []).filter((d) => d.id != null);
                  // send PUT via helper to update only existing server records; do not POST new items here
                  const result = await putUpdatedCallTypes(data, deletedIds);
                  // eslint-disable-next-line no-console
                  console.log('Put update response', result);
                  // clear deletedIds after success
                  setDeletedIds([]);
                  // pass updated tree to parent via onSave (convert to API node shape)
                  const apiTree = (current || []).map((n, i) => mapToApiNode(n, i));
                  if (typeof onSave === 'function') onSave({ ...field, tree: apiTree });
                  onClose();
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('Failed to update CallType', err);
                  setError(String(err));
                } finally {
                  setLoading(false);
                }
              })();
            }}
            className="primary-button"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
